import { mean, standardDeviation, zScore } from "simple-statistics";
import { chartExportableOptions } from "../mapperUtil.js";

const Z_SCORE_PLOT_LINES: Highcharts.XAxisPlotLinesOptions[] = [-3, -2, -1, 0, 1, 2, 3].map(
  (v) => ({
    value: v,
    color: "rgba(0, 0, 0, 0.25)",
    width: 1,
    dashStyle: v === 0 ? "Solid" : "ShortDash",
    zIndex: 2,
  })
);

const pooledStats = (values: number[]): { mean: number; std: number } => ({
  mean: mean(values),
  std: standardDeviation(values),
});

const toZScores = (values: number[], mu: number, sigma: number): number[] =>
  sigma === 0 ? values.map(() => 0) : values.map((v) => zScore(v, mu, sigma));

const standardNormalPdf = (z: number): number =>
  (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * z * z);

// When there is only one data point, Highcharts cannot compute std for the bell curve.
// Synthesise two flanking points at ±1 so the curve renders as a standard normal shape.
const bellBaseData = (zScores: number[]): number[] =>
  zScores.length >= 2 ? zScores : [zScores[0] - 1, zScores[0], zScores[0] + 1];

// The injured entries stay on the chart as points, but they never enter the pooled
// statistics or the drawn curve on a team view. With no healthy entry at all the curve
// is skipped and every point sits at z = 0.
const healthyPairs = (pairs: ValidPair[]): ValidPair[] => pairs.filter((p) => !p.injured);

const obsPoint = (p: ValidPair, z: number) => ({
  x: z,
  y: 0,
  realValue: p.rawValue,
  date: p.item.date,
  playerName: p.playerName,
});

const obsTooltipFormatter = function (this: any): string | false {
  const pt = this.point as any;
  if (pt.realValue == null) return false;
  return (
    `<b>${pt.playerName ?? pt.date ?? ""}</b>` +
    (pt.playerName && pt.date ? `<br/>Date: ${pt.date}` : "") +
    `<br/>Real Value: ${pt.realValue}` +
    `<br/>Z-Score: ${Number(pt.x).toFixed(2)}` +
    `<br/>Prob. Density: ${standardNormalPdf(pt.x).toFixed(4)}`
  );
};

// Toggles the paired bell curve (by id) when a scatter legend item is clicked.
// Does NOT prevent the default so Highcharts handles the scatter toggle itself.
// Bell curve visibility is derived from its own current state, not the scatter's,
// so there is no ambiguity about whether this.visible is pre- or post-toggle.
const makeLegendClickHandler = (bellId: string, showCurve: boolean) =>
  function (this: any): void {
    if (!showCurve) return;
    const bellSeries = this.chart.series.find(
      (s: any) => s.options?.id === bellId
    ) as any;
    if (!bellSeries) return;
    bellSeries.visible ? bellSeries.hide() : bellSeries.show();
  };

// ---------------------------------------------------------------------------

type ValidPair = { item: any; rawValue: number; playerName?: string; injured: boolean };

const extractValidPairs = (
  rawData: any[],
  metricName: string,
  stat: string | null | undefined,
  lowGood: boolean
): ValidPair[] => {
  const pairs: ValidPair[] = [];
  for (const item of rawData) {
    if (Array.isArray(item.non_aggregated_data) && item.non_aggregated_data.length > 0) {
      for (const entry of item.non_aggregated_data) {
        const n = entry.value != null && !isNaN(Number(entry.value)) ? Number(entry.value) : null;
        if (n !== null) {
          pairs.push({
            item,
            rawValue: n,
            playerName: entry.first_name && entry.last_name
              ? `${entry.first_name} ${entry.last_name}`
              : undefined,
            injured: entry.injured === true,
          });
        }
      }
    } else {
      let v: any;
      if (typeof stat === "string" && stat.toLowerCase() === "max") {
        v = lowGood ? item[`${metricName}__min`] : item[`${metricName}__max`];
      } else if (typeof stat === "string" && stat.toLowerCase() === "sum") {
        v = item[`${metricName}__sum`];
      } else {
        v = item[metricName];
      }
      const n = v != null && !isNaN(Number(v)) ? Number(v) : null;
      if (n !== null) pairs.push({ item, rawValue: n, injured: item.injured === true });
    }
  }
  return pairs;
};

// ---------------------------------------------------------------------------

// `teamView` is true on a team page, where the chart pools several players and the
// injured entries are left out of the statistics. On a player page the entries are the
// player's own history and every one counts.
export const mapDataForCognitionBellCurve = ({
  dataIn,
  metric,
  stat = null,
  showTitle = true,
  teamView = false,
}: {
  dataIn: any;
  metric: any;
  stat?: string | null;
  showTitle?: boolean;
  teamView?: boolean;
}): Highcharts.Options | null => {
  const metricName: string = metric?.name;
  if (!metricName) return null;

  const validPairs = extractValidPairs(dataIn?.data || [], metricName, stat, metric.low_good);
  if (validPairs.length < 1) return null;

  const statPairs = teamView ? healthyPairs(validPairs) : validPairs;
  const hasStats = statPairs.length >= 1;
  const { mean: mu, std: sigma } = hasStats
    ? pooledStats(statPairs.map((p) => p.rawValue))
    : { mean: 0, std: 0 };
  const zScores = toZScores(validPairs.map((p) => p.rawValue), mu, sigma);
  const baseZScores = toZScores(statPairs.map((p) => p.rawValue), mu, sigma);

  const obsData = validPairs.map((p, i) => obsPoint(p, zScores[i]));

  const curveSeries: Highcharts.SeriesOptionsType[] = hasStats
    ? [
        {
          name: metricName,
          type: "bellcurve",
          baseSeries: "cognition-bell-base",
          xAxis: 0,
          yAxis: 0,
          zIndex: -1,
          fillOpacity: 0.2,
          showInLegend: false,
          enableMouseTracking: false,
        } as Highcharts.SeriesBellcurveOptions,
        {
          type: "scatter",
          id: "cognition-bell-base",
          data: bellBaseData(baseZScores),
          xAxis: 1,
          yAxis: 1,
          visible: false,
          showInLegend: false,
          enableMouseTracking: false,
        } as Highcharts.SeriesScatterOptions,
      ]
    : [];

  return {
    chart: { type: "bellcurve" },
    title: { text: showTitle ? metricName : "" },
    xAxis: [
      { title: { text: "Z-Score" }, alignTicks: false, plotLines: Z_SCORE_PLOT_LINES },
      { visible: false, alignTicks: false },
    ],
    yAxis: [
      { title: { text: "Probability Density" } },
      { visible: false, min: -1, max: 1 },
    ],
    legend: { enabled: false },
    tooltip: { useHTML: true, formatter: obsTooltipFormatter },
    series: [
      ...curveSeries,
      {
        type: "scatter",
        name: metricName,
        data: obsData,
        xAxis: 0,
        yAxis: 1,
        showInLegend: false,
        opacity: 0.7,
        marker: { radius: 3.75, symbol: "circle" },
        jitter: { y: 1 },
      } as any,
    ],
    exporting: { enabled: true, ...chartExportableOptions },
  };
};

// ---------------------------------------------------------------------------

export const mapDataForTeamCognitionBellCurve = ({
  subjects,
  metric,
  showCurve = true,
}: {
  subjects: Array<{ label: string; data: any; stat?: string | null }>;
  metric: any;
  showCurve?: boolean;
}): Highcharts.Options | null => {
  if (!subjects.length || !metric?.name) return null;

  const metricName: string = metric.name;

  const parsed = subjects.map(({ label, data, stat }) => ({
    label,
    stat,
    validPairs: extractValidPairs(data?.data || [], metricName, stat, metric.low_good),
  }));

  const allRaw = parsed.flatMap((p) => p.validPairs.map((vp) => vp.rawValue));
  if (allRaw.length < 1) return null;
  const allHealthy = parsed.flatMap((p) => healthyPairs(p.validPairs).map((vp) => vp.rawValue));
  const hasStats = allHealthy.length >= 1;
  const { mean: mu, std: sigma } = hasStats ? pooledStats(allHealthy) : { mean: 0, std: 0 };

  const series: Highcharts.SeriesOptionsType[] = [];
  let hasData = false;

  parsed.forEach(({ label, validPairs }, idx) => {
    if (validPairs.length < 1) return;
    hasData = true;

    const zScores = toZScores(validPairs.map((p) => p.rawValue), mu, sigma);
    const healthy = healthyPairs(validPairs);
    const bellId = `team-bell-${idx}`;

    if (hasStats && healthy.length >= 1) {
      series.push({
        type: "scatter",
        id: `team-bell-base-${idx}`,
        data: bellBaseData(toZScores(healthy.map((p) => p.rawValue), mu, sigma)),
        xAxis: 1,
        yAxis: 2,
        visible: false,
        showInLegend: false,
        enableMouseTracking: false,
      } as Highcharts.SeriesScatterOptions);

      series.push({
        type: "bellcurve",
        id: bellId,
        name: label,
        baseSeries: `team-bell-base-${idx}`,
        xAxis: 0,
        yAxis: 0,
        zIndex: -1,
        fillOpacity: 0.2,
        visible: showCurve,
        showInLegend: false,
        enableMouseTracking: false,
      } as Highcharts.SeriesBellcurveOptions);
    }

    const obsData = validPairs.map((p, i) => obsPoint(p, zScores[i]));

    series.push({
      type: "scatter",
      name: label,
      data: obsData,
      xAxis: 0,
      yAxis: 1,
      visible: true,
      showInLegend: true,
      opacity: 0.7,
      marker: { radius: 3.75, symbol: "circle" },
      jitter: { y: 1 },
      events: { legendItemClick: makeLegendClickHandler(bellId, showCurve) },
    } as any);
  });

  if (!hasData) return null;

  return {
    chart: { type: "bellcurve" },
    title: { text: metricName },
    xAxis: [
      { title: { text: "Z-Score" }, alignTicks: false, plotLines: Z_SCORE_PLOT_LINES },
      { visible: false, alignTicks: false },
    ],
    yAxis: [
      { title: { text: "Probability Density" }, min: 0, max: 0.45 },
      { visible: false, min: -1, max: 1 },
      { visible: false },
    ],
    legend: { enabled: true },
    tooltip: { useHTML: true, formatter: obsTooltipFormatter },
    series,
    exporting: { enabled: true, ...chartExportableOptions },
  };
};

// ---------------------------------------------------------------------------

// The single-player chart: the injured dates are the player's own history, so every
// entry counts and no entry is marked.
export const mapDataForMultiCognitionBellCurve = ({
  seriesData,
  showTitle = true,
  showCurve = true,
  title,
}: {
  seriesData: Array<{ metric: any; data: any; stat?: string | null }>;
  showTitle?: boolean;
  showCurve?: boolean;
  title?: string;
}): Highcharts.Options | null => {
  if (!seriesData.length) return null;

  const parsed = seriesData
    .map(({ metric, data, stat }) => {
      if (!metric?.name) return null;
      return {
        metric,
        stat,
        validPairs: extractValidPairs(data?.data || [], metric.name, stat, metric.low_good),
      };
    })
    .filter(Boolean) as Array<{ metric: any; stat: any; validPairs: ValidPair[] }>;

  const allRaw = parsed.flatMap((p) => p.validPairs.map((vp) => vp.rawValue));
  if (allRaw.length < 1) return null;
  const { mean: mu, std: sigma } = pooledStats(allRaw);

  const series: Highcharts.SeriesOptionsType[] = [];

  parsed.forEach(({ metric, validPairs }, idx) => {
    if (validPairs.length < 1) return;

    const metricName: string = metric.name;
    const zScores = toZScores(validPairs.map((p) => p.rawValue), mu, sigma);
    const bellId = `cognition-bell-${idx}`;

    series.push({
      type: "scatter",
      id: `cognition-bell-base-${idx}`,
      data: bellBaseData(zScores),
      xAxis: 1,
      yAxis: 2,
      visible: false,
      showInLegend: false,
      enableMouseTracking: false,
    } as Highcharts.SeriesScatterOptions);

    series.push({
      type: "bellcurve",
      id: bellId,
      name: metricName,
      baseSeries: `cognition-bell-base-${idx}`,
      xAxis: 0,
      yAxis: 0,
      zIndex: -1,
      fillOpacity: 0.2,
      visible: showCurve,
      showInLegend: false,
      enableMouseTracking: false,
    } as Highcharts.SeriesBellcurveOptions);

    const obsData = validPairs.map((p, i) => ({
      x: zScores[i],
      y: 0,
      realValue: p.rawValue,
      date: p.item.date,
      playerName: p.playerName,
    }));

    series.push({
      type: "scatter",
      name: metricName,
      data: obsData,
      xAxis: 0,
      yAxis: 1,
      visible: true,
      showInLegend: true,
      opacity: 0.7,
      marker: { radius: 3.75, symbol: "circle" },
      jitter: { y: 1 },
      events: { legendItemClick: makeLegendClickHandler(bellId, showCurve) },
    } as any);
  });

  if (!series.length) return null;

  return {
    chart: { type: "bellcurve" },
    title: { text: showTitle ? (title ?? "Cognition") : "" },
    xAxis: [
      { title: { text: "Z-Score" }, alignTicks: false, plotLines: Z_SCORE_PLOT_LINES },
      { visible: false, alignTicks: false },
    ],
    yAxis: [
      { title: { text: "Probability Density" }, min: 0, max: 0.45 },
      { visible: false, min: -1, max: 1 },
      { visible: false },
    ],
    legend: { enabled: true },
    tooltip: { useHTML: true, formatter: obsTooltipFormatter },
    series,
    exporting: { enabled: true, ...chartExportableOptions },
  };
};
